import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { usePlan } from "../../hooks/usePlan";
import { TemplateSelector } from "./TemplateSelector";
import { PlanMilestone, PlanMilestoneFormData, ProjectTemplate } from "../../pages/contractor/planTypes";

interface Props {
  projectId: string;
}

const emptyForm: PlanMilestoneFormData = {
  title: "",
  description: "",
  percentage_of_project: "",
  estimated_cost: "",
  target_date: "",
};

const planStatusLabels = {
  draft: "Draft",
  pending_approval: "Awaiting Client Approval",
  approved: "Approved",
};

const planStatusColors: Record<string, "default" | "warning" | "success"> = {
  draft: "default",
  pending_approval: "warning",
  approved: "success",
};

export function PlanningTab({ projectId }: Props) {
  const {
    plan,
    templates,
    loading,
    templatesLoading,
    saving,
    error,
    fetchPlan,
    fetchTemplates,
    savePlanDetails,
    loadTemplate,
    addPlanMilestone,
    updatePlanMilestone,
    deletePlanMilestone,
    reorderPlanMilestones,
    submitForApproval,
  } = usePlan(projectId);

  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<PlanMilestone | null>(null);
  const [formData, setFormData] = useState<PlanMilestoneFormData>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [scopeText, setScopeText] = useState("");
  const [notesText, setNotesText] = useState("");
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [confirmTemplateLoad, setConfirmTemplateLoad] = useState<ProjectTemplate | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId || !plan) return;
    const from = plan.milestones.findIndex((m) => m.id === dragId);
    const to = plan.milestones.findIndex((m) => m.id === targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...plan.milestones];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setDragId(null);
    reorderPlanMilestones(reordered);
  };

  useEffect(() => {
    fetchPlan();
    fetchTemplates();
  }, [fetchPlan, fetchTemplates]);

  useEffect(() => {
    if (plan) {
      setScopeText(plan.plan_scope ?? "");
      setNotesText(plan.plan_notes ?? "");
    }
  }, [plan]);

  const totalPercent = plan?.milestones.reduce(
    (sum, m) => sum + Number(m.percentage_of_project),
    0,
  ) ?? 0;

  const totalEstimatedCost = plan?.milestones.reduce(
    (sum, m) => sum + Number(m.estimated_cost ?? 0),
    0,
  ) ?? 0;

  const isEditable = plan?.plan_status === "draft";

  const openCreate = () => {
    setEditingMilestone(null);
    setFormData(emptyForm);
    setFormError(null);
    setMilestoneDialogOpen(true);
  };

  const openEdit = (m: PlanMilestone) => {
    setEditingMilestone(m);
    setFormData({
      title: m.title,
      description: m.description ?? "",
      percentage_of_project: m.percentage_of_project.toString(),
      estimated_cost: m.estimated_cost?.toString() ?? "",
      target_date: m.target_date ?? "",
    });
    setFormError(null);
    setMilestoneDialogOpen(true);
  };

  const handleMilestoneSave = async () => {
    if (!formData.title.trim()) {
      setFormError("Title is required.");
      return;
    }
    const pct = Number(formData.percentage_of_project);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setFormError("Percentage must be between 0 and 100.");
      return;
    }

    let ok: boolean;
    if (editingMilestone) {
      ok = await updatePlanMilestone(editingMilestone.id, formData);
    } else {
      ok = await addPlanMilestone(formData, plan?.milestones ?? []);
    }

    if (ok) setMilestoneDialogOpen(false);
  };

  const handleSaveDetails = useCallback(async () => {
    const ok = await savePlanDetails(scopeText, notesText);
    if (ok) {
      setDetailsSaved(true);
      setTimeout(() => setDetailsSaved(false), 2000);
    }
  }, [savePlanDetails, scopeText, notesText]);

  const handleTemplateSelect = async (template: ProjectTemplate) => {
    if ((plan?.milestones.length ?? 0) > 0) {
      setConfirmTemplateLoad(template);
      setTemplateSelectorOpen(false);
      return;
    }
    setTemplateSelectorOpen(false);
    await loadTemplate(template);
  };

  const handleConfirmTemplateLoad = async () => {
    if (!confirmTemplateLoad) return;
    await loadTemplate(confirmTemplateLoad);
    setConfirmTemplateLoad(null);
  };

  const handleSubmitForApproval = async () => {
    setSubmitConfirmOpen(false);
    await submitForApproval();
  };

  const handleDeleteMilestone = async () => {
    if (!deleteConfirmId) return;
    await deletePlanMilestone(deleteConfirmId);
    setDeleteConfirmId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Project Plan
          </Typography>
          {plan && (
            <Chip
              label={planStatusLabels[plan.plan_status]}
              color={planStatusColors[plan.plan_status]}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        {isEditable && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={saving || (plan?.milestones.length ?? 0) === 0}
            onClick={() => setSubmitConfirmOpen(true)}
          >
            Send to client for approval
          </Button>
        )}
        {plan?.plan_status === "pending_approval" && (
          <Chip label="Waiting on client" color="warning" />
        )}
        {plan?.plan_status === "approved" && plan.plan_approved_at && (
          <Typography variant="caption" color="text.secondary">
            Approved {new Date(plan.plan_approved_at).toLocaleDateString()}
          </Typography>
        )}
      </Box>

      {/* Scope & Notes */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Scope of Work
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Project scope"
              multiline
              minRows={3}
              fullWidth
              value={scopeText}
              onChange={(e) => setScopeText(e.target.value)}
              disabled={!isEditable}
              placeholder="Describe the overall scope of this project — what will be done, what areas are included..."
            />
            <TextField
              label="Assumptions & exclusions"
              multiline
              minRows={2}
              fullWidth
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              disabled={!isEditable}
              placeholder="List any assumptions made or work that is explicitly excluded from this plan..."
            />
            {isEditable && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={handleSaveDetails}
                  disabled={saving}
                >
                  {detailsSaved ? "Saved" : "Save details"}
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Milestones */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Planned Milestones
              </Typography>
              {plan && plan.milestones.length > 0 && (
                <Typography variant="caption" color={totalPercent === 100 ? "success.main" : "warning.main"}>
                  {totalPercent}% allocated
                  {totalEstimatedCost > 0 && ` · $${totalEstimatedCost.toLocaleString()} estimated`}
                </Typography>
              )}
            </Box>
            {isEditable && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    fetchTemplates();
                    setTemplateSelectorOpen(true);
                  }}
                >
                  Load template
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  Add milestone
                </Button>
              </Stack>
            )}
          </Box>

          {(plan?.milestones.length ?? 0) === 0 ? (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No milestones added yet.
              </Typography>
              {isEditable && (
                <Typography variant="body2" color="text.secondary">
                  Load a template or add milestones manually.
                </Typography>
              )}
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {isEditable && (
                <Typography variant="caption" color="text.secondary">
                  Drag milestones to reorder.
                </Typography>
              )}
              {plan!.milestones.map((m, idx) => (
                <Box
                  key={m.id}
                  draggable={isEditable}
                  onDragStart={() => isEditable && setDragId(m.id)}
                  onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => isEditable && handleDrop(m.id)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: dragId === m.id ? "primary.main" : "divider",
                    borderRadius: 1,
                    opacity: dragId === m.id ? 0.5 : 1,
                    cursor: isEditable ? "grab" : "default",
                  }}
                >
                  {isEditable && (
                    <DragIcon
                      fontSize="small"
                      sx={{ color: "text.disabled", mt: 0.5, flexShrink: 0 }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {m.title}
                    </Typography>
                    {m.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {m.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        {m.percentage_of_project}% of project
                      </Typography>
                      {m.estimated_cost && (
                        <Typography variant="caption" color="text.secondary">
                          · ${Number(m.estimated_cost).toLocaleString()} est.
                        </Typography>
                      )}
                      {m.target_date && (
                        <Typography variant="caption" color="text.secondary">
                          · Target: {new Date(m.target_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  {isEditable && (
                    <Stack direction="row" spacing={0.5} flexShrink={0}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(m)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteConfirmId(m.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Template Selector */}
      <TemplateSelector
        open={templateSelectorOpen}
        templates={templates}
        loading={templatesLoading}
        onSelect={handleTemplateSelect}
        onClose={() => setTemplateSelectorOpen(false)}
      />

      {/* Confirm template overwrite */}
      <Dialog open={!!confirmTemplateLoad} onClose={() => setConfirmTemplateLoad(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Replace existing milestones?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Loading "{confirmTemplateLoad?.name}" will replace your current plan milestones. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmTemplateLoad(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmTemplateLoad} disabled={saving}>
            Replace milestones
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Milestone Dialog */}
      <Dialog open={milestoneDialogOpen} onClose={() => setMilestoneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMilestone ? "Edit milestone" : "Add milestone"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
            <TextField
              label="Description"
              multiline
              minRows={2}
              fullWidth
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="% of project"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={formData.percentage_of_project}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, percentage_of_project: e.target.value }))
                }
              />
              <TextField
                label="Estimated cost ($)"
                type="number"
                inputProps={{ min: 0 }}
                value={formData.estimated_cost}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, estimated_cost: e.target.value }))
                }
              />
            </Box>
            <TextField
              label="Target date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.target_date}
              onChange={(e) => setFormData((prev) => ({ ...prev, target_date: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMilestoneDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMilestoneSave} disabled={saving}>
            {editingMilestone ? "Save changes" : "Add milestone"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit for approval confirm */}
      <Dialog open={submitConfirmOpen} onClose={() => setSubmitConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send plan for client approval?</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              The client will be notified and can review, comment on, or approve the plan.
            </Typography>
            {totalPercent !== 100 && (
              <Alert severity="warning">
                Your milestones add up to {totalPercent}%, not 100%. Consider adjusting before sending.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSubmitConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitForApproval} disabled={saving}>
            Send for approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete milestone confirm */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete milestone?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteMilestone} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
